import { useI18n } from "@/hooks/useI18n";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SEOHead } from "@/components/SEOHead";
import { 
  Truck, 
  Thermometer, 
  Package, 
  Plane, 
  Clock,
  MapPin,
  CheckCircle,
  Calculator
} from "lucide-react";

import { useDrawers } from "@/hooks/useDrawers";

export default function Logistics() {
  const { t } = useI18n();
  const { openRFQ } = useDrawers();

  const logisticsFlow = [
    {
      step: 1,
      title: "Collection",
      description: "Temperature-controlled collection from partner farms",
      icon: Truck,
      details: ["0-4°C transport vehicles", "GPS tracking", "Collection scheduling", "Farm coordination"]
    },
    {
      step: 2,
      title: "Pre-cooling",
      description: "Immediate pre-cooling at collection centers",
      icon: Thermometer,
      details: ["Rapid cooling to 0-4°C", "Humidity control", "Quality inspection", "Temperature logging"]
    },
    {
      step: 3,
      title: "Consolidation",
      description: "Order consolidation and final packing",
      icon: Package,
      details: ["Order verification", "Packing by standards", "Hydration application", "Final QC check"]
    },
    {
      step: 4,
      title: "UIO Airport",
      description: "Export processing and documentation",
      icon: Plane,
      details: ["Customs clearance", "Phyto certificate", "AWB processing", "Loading coordination"]
    },
    {
      step: 5,
      title: "Worldwide Arrival",
      description: "Quick retrieval and cold storage",
      icon: MapPin,
      details: ["Priority handling", "Cold room storage", "≤2 hour window", "Quality verification"]
    },
    {
      step: 6,
      title: "Delivery",
      description: "Final delivery to customer location",
      icon: Truck,
      details: ["Temperature-controlled delivery", "Customer notification", "Delivery confirmation", "Quality feedback"]
    }
  ];

  const packingStandards = [
    { product: "Roses 40cm", bunches: "25 stems/bunch", box: "25 bunches/box", total: "625 stems/box" },
    { product: "Roses 50cm", bunches: "25 stems/bunch", box: "20 bunches/box", total: "500 stems/box" },
    { product: "Roses 60cm", bunches: "25 stems/bunch", box: "16 bunches/box", total: "400 stems/box" },
    { product: "Roses 70cm", bunches: "25 stems/bunch", box: "12 bunches/box", total: "300 stems/box" },
    { product: "Roses 80cm", bunches: "25 stems/bunch", box: "10 bunches/box", total: "250 stems/box" },
    { product: "Roses 90cm", bunches: "25 stems/bunch", box: "8 bunches/box", total: "200 stems/box" },
    { product: "Roses 100cm", bunches: "25 stems/bunch", box: "6 bunches/box", total: "150 stems/box" },
    { product: "Roses 120cm", bunches: "25 stems/bunch", box: "5 bunches/box", total: "125 stems/box" },
    { product: "Ranunculus 30cm", bunches: "10 stems/bunch", box: "35 bunches/box", total: "350 stems/box" },
    { product: "Ranunculus 40cm", bunches: "10 stems/bunch", box: "30 bunches/box", total: "300 stems/box" },
    { product: "Ranunculus 50cm", bunches: "10 stems/bunch", box: "25 bunches/box", total: "250 stems/box" },
    { product: "Ranunculus 60cm", bunches: "10 stems/bunch", box: "20 bunches/box", total: "200 stems/box" }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-andean text-andean-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-display font-light mb-6 tracking-wide">
              {t('page.logistics.title')}
            </h1>
            <p className="text-lg md:text-xl mb-8 text-andean-cream/90 font-light leading-relaxed">
              Advanced cold-chain logistics ensuring your flowers arrive worldwide at perfect quality with complete temperature control
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Badge variant="secondary" className="bg-andean-cream/20 text-andean-cream border-andean-cream/30">
                <Thermometer className="w-4 h-4 mr-1" />
                0–4°C Guaranteed
              </Badge>
              <Badge variant="secondary" className="bg-andean-cream/20 text-andean-cream border-andean-cream/30">
                <Clock className="w-4 h-4 mr-1" />
                ≤2 Hour Window
              </Badge>
              <Badge variant="secondary" className="bg-andean-cream/20 text-andean-cream border-andean-cream/30">
                <CheckCircle className="w-4 h-4 mr-1" />
                Full Tracking
              </Badge>
            </div>
          </div>
        </div>
      </section>

      {/* Logistics Flow */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl font-display font-light text-foreground mb-4 tracking-wide">
              Cold-Chain Logistics Flow
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              From farm collection to worldwide delivery, every step maintains optimal temperature and quality
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {logisticsFlow.map((step, index) => (
              <Card key={index} className="relative h-full hover:shadow-andean transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-andean rounded-full flex items-center justify-center">
                      <step.icon className="w-6 h-6 text-andean-cream" />
                    </div>
                    <Badge variant="outline" className="text-sm">
                      Step {step.step}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl">{step.title}</CardTitle>
                  <CardDescription className="text-base">
                    {step.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {step.details.map((detail, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-andean-forest flex-shrink-0" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>


      {/* Reception Window */}
      <section className="py-20 bg-gradient-andean text-andean-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-display font-light mb-6 tracking-wide">
              Worldwide Reception Window
            </h2>
            <p className="text-xl mb-8 text-andean-cream/90">
              Our logistics partners worldwide ensure flowers are received within 2 hours of arrival and stored in cold-room facilities
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-andean-cream/10 p-6 rounded-lg">
                <Clock className="w-8 h-8 mx-auto mb-3" />
                <h3 className="font-light mb-2 tracking-wide">≤2 Hour Window</h3>
                <p className="text-sm text-andean-cream/80 font-light">
                  Guaranteed pickup within 2 hours of flight arrival
                </p>
              </div>
              <div className="bg-andean-cream/10 p-6 rounded-lg">
                <Thermometer className="w-8 h-8 mx-auto mb-3" />
                <h3 className="font-light mb-2 tracking-wide">Cold Storage</h3>
                <p className="text-sm text-andean-cream/80 font-light">
                  Immediate transfer to 0-4°C cold room facilities
                </p>
              </div>
              <div className="bg-andean-cream/10 p-6 rounded-lg">
                <CheckCircle className="w-8 h-8 mx-auto mb-3" />
                <h3 className="font-light mb-2 tracking-wide">Quality Check</h3>
                <p className="text-sm text-andean-cream/80 font-light">
                  Arrival quality inspection and documentation
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}